import { makeStyles } from '@material-ui/core';
import {
  DevHubBox,
  DevHubCard,
  DevHubTypography,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubAlert,
} from '@devxdiscover/devhub-ui';
import { ExperimentalApplication } from '../types';
import { IdeaMetadata } from './IdeaMetadata';

const useStyles = makeStyles(theme => ({
  deletionCard: {
    border: '2px solid #f44336',
    backgroundColor: '#ffebee',
  },
  resourceList: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  statusIcon: {
    fontSize: '1.2rem',
  },
  removed: {
    color: '#666',
    textDecoration: 'line-through',
  },
  failed: {
    color: '#f44336',
    fontWeight: 600,
  },
  errorMessage: {
    fontSize: '0.85rem',
    color: '#d32f2f',
    marginLeft: theme.spacing(3),
    fontStyle: 'italic',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

interface FailedDeletionCardProps {
  idea: ExperimentalApplication;
  failedResources?: string[];
  errors?: Record<string, string>;
  onResume: () => void;
  onCancel: () => void;
  resuming?: boolean;
}

/**
 * Card component for displaying a failed deletion attempt
 * Shows distinct UI from normal cards to indicate deletion is incomplete
 * @param props - Component props
 * @param props.idea - Experimental application that failed to delete
 * @param props.failedResources - Array of resource types that failed to delete
 * @param props.errors - Error messages per resource type
 * @param props.onResume - Callback to resume deletion attempt
 * @param props.onCancel - Callback to cancel deletion and keep app
 * @param props.resuming - Whether deletion is being resumed
 */
export const FailedDeletionCard = ({
  idea,
  failedResources = [],
  errors = {},
  onResume,
  onCancel,
  resuming = false,
}: FailedDeletionCardProps) => {
  const classes = useStyles();

  // Determine which resources were successfully removed vs failed
  // Only include resources that were actually part of the idea (successfully provisioned)
  const allResources = {
    github: idea.resources.github
      ? {
          name: 'GitHub Repository',
          removed:
            !idea.resources.github && !failedResources.includes('github'),
          failed: failedResources.includes('github'),
          error: errors.github,
        }
      : null,
    cnap: idea.resources.cnap
      ? {
          name: 'CNAP Tenancy',
          removed: !idea.resources.cnap && !failedResources.includes('cnap'),
          failed: failedResources.includes('cnap'),
          error: errors.cnap,
        }
      : null,
    adGroups:
      idea.resources.adGroups && idea.resources.adGroups.length > 0
        ? {
            name: 'AD Groups',
            removed:
              idea.resources.adGroups?.length === 0 &&
              !failedResources.includes('adGroups'),
            failed: failedResources.includes('adGroups'),
            error: errors.adGroups,
          }
        : null,
  };

  // Filter out null resources (ones that were never provisioned)
  const resources = Object.fromEntries(
    Object.entries(allResources).filter(([_, resource]) => resource !== null),
  ) as Record<
    string,
    NonNullable<(typeof allResources)[keyof typeof allResources]>
  >;

  // Check if any resources were actually removed
  // Only allow "Keep App" if nothing was deleted (all deletions failed)
  const anyResourcesRemoved = Object.values(resources).some(
    resource => resource.removed,
  );

  return (
    <DevHubCard
      title={idea.name}
      body={
        <DevHubBox padding="medium">
          {idea.description && (
            <DevHubTypography
              variant="body1"
              style={{ marginBottom: '16px', color: '#666' }}
            >
              {idea.description}
            </DevHubTypography>
          )}
          <IdeaMetadata
            applicationId={idea.applicationId}
            ownerText={`${idea.owner.firstName} ${idea.owner.lastName}`}
            variant="medium"
          />
          <DevHubTypography
            variant="body2"
            style={{
              marginBottom: '16px',
              display: 'block',
              color: '#888',
            }}
          >
            Created: {new Date(idea.createdAt).toLocaleDateString()}
          </DevHubTypography>

          <DevHubAlert severity="warning">
            <DevHubTypography variant="body2">
              Some resources couldn't be removed. You can retry deletion or keep
              the application.
            </DevHubTypography>
          </DevHubAlert>

          <DevHubTypography
            variant="subtitle2"
            style={{ marginTop: '16px', fontWeight: 600 }}
          >
            Deletion Status:
          </DevHubTypography>

          <div className={classes.resourceList}>
            {Object.entries(resources).map(([key, resource]) => (
              <div key={key}>
                <div className={classes.resourceItem}>
                  {resource.removed && (
                    <>
                      <span className={classes.statusIcon}>✅</span>
                      <DevHubTypography
                        variant="body2"
                        className={classes.removed}
                      >
                        {resource.name} (removed)
                      </DevHubTypography>
                    </>
                  )}
                  {resource.failed && (
                    <>
                      <span className={classes.statusIcon}>❌</span>
                      <DevHubTypography
                        variant="body2"
                        className={classes.failed}
                      >
                        {resource.name} (failed to remove)
                      </DevHubTypography>
                    </>
                  )}
                  {!resource.removed && !resource.failed && (
                    <>
                      <span className={classes.statusIcon}>⚠️</span>
                      <DevHubTypography
                        variant="body2"
                        style={{ color: '#757575' }}
                      >
                        {resource.name} (not attempted)
                      </DevHubTypography>
                    </>
                  )}
                </div>
                {resource.error && (
                  <DevHubTypography className={classes.errorMessage}>
                    {typeof resource.error === 'string'
                      ? resource.error
                      : JSON.stringify(resource.error)}
                  </DevHubTypography>
                )}
              </div>
            ))}
          </div>

          <div className={classes.actionButtons}>
            <DevHubButton
              variant="contained"
              color="secondary"
              onClick={onResume}
              disabled={resuming}
              startIcon={
                resuming ? undefined : <DevHubDynamicIcon iconName="Sync" />
              }
            >
              {resuming ? 'Retrying...' : 'Retry Delete'}
            </DevHubButton>
            {!anyResourcesRemoved && (
              <DevHubButton
                variant="outlined"
                onClick={onCancel}
                disabled={resuming}
              >
                Keep App
              </DevHubButton>
            )}
          </div>
        </DevHubBox>
      }
    />
  );
};
